import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Grid,
    TextField,
    Typography,
    IconButton,
    useMediaQuery,
    useTheme,
    Paper,
    CircularProgress,
    Card,
    CardContent,
    Chip,
    Divider,
    Fade,
    Collapse,
    Alert,
    Stepper,
    Step,
    StepLabel,
    Container,
    Stack,
    FormControlLabel,
    Radio,
    RadioGroup,
    Tooltip,
    Fab,
} from "@mui/material";
import {
    Close as CloseIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    PlayCircleOutline as VideoIcon,
    Quiz as QuizIcon,
    School as SchoolIcon,
    CheckCircle as CheckIcon,
    Error as ErrorIcon,
    Info as InfoIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";

const MicroLearningForm = ({
    registerData = { topic: '', name: '', json_data: { questions: [] } },
    handleChange = () => {},
    handleCloseModal = () => {},
    handleSubmit = () => {},
    topics = [],
    editData = false,
    clientCodes = []
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const [questions, setQuestions] = useState([
        {
            questionText: "",
            options: ["", ""],
            answerIndex: null,
            explanation: "",
        }
    ]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedClient, setSelectedClient] = useState(clientCodes[0]?.name || '');
    const [videoFile, setVideoFile] = useState('');
    const [activeStep, setActiveStep] = useState(0);
    const [expandedQuestion, setExpandedQuestion] = useState(0);
    const [validationErrors, setValidationErrors] = useState({});
    const [formProgress, setFormProgress] = useState(0);
    
    const currentUser = "Current User"; // Fallback since localStorage not available

    const steps = ['Basic Info', 'Video Upload', 'Questions', 'Review'];

    useEffect(() => {
        if (registerData?.json_data?.questions?.length) {
            setQuestions(registerData.json_data.questions);
        }
    }, [registerData]);

    useEffect(() => {
        calculateProgress();
    }, [registerData, questions, selectedClient]);

    const calculateProgress = () => {
        let progress = 0;
        if (registerData.name) progress += 25;
        if (registerData.topic && selectedClient) progress += 25;
        if (videoFile) progress += 25;
        if (questions.some(q => isQuestionValid(q))) progress += 25;
        setFormProgress(progress);
    };

    const isQuestionValid = (q) => {
        return (
            q.questionText.trim() &&
            q.options.length >= 2 &&
            q.options.every((opt) => opt.trim() !== '') &&
            typeof q.answerIndex === 'number' &&
            q.answerIndex >= 0 &&
            q.answerIndex < q.options.length
        );
    };

    const validateForm = () => {
        const errors = {};
        
        if (!registerData.name?.trim()) {
            errors.name = 'Video title is required';
        }
        
        if (!registerData.topic) {
            errors.topic = 'Please select a topic';
        }
        
        if (!selectedClient) {
            errors.client = 'Please select a client';
        }

        questions.forEach((q, index) => {
            if (!q.questionText.trim()) {
                errors[`question_${index}`] = 'Question text is required';
            }
            if (q.options.length < 2) {
                errors[`options_${index}`] = 'At least 2 options required';
            }
            if (q.answerIndex === null) {
                errors[`answer_${index}`] = 'Please select correct answer';
            }
        });

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleAddQuestion = () => {
        setQuestions([
            ...questions,
            {
                questionText: "",
                options: ["", ""],
                answerIndex: null,
                explanation: "",
            },
        ]);
        setExpandedQuestion(questions.length);
    };

    const handleQuestionChange = (index, field, value) => {
        const updated = [...questions];
        updated[index][field] = value;
        setQuestions(updated);
        
        // Clear validation error when user starts typing
        if (validationErrors[`question_${index}`]) {
            const newErrors = { ...validationErrors };
            delete newErrors[`question_${index}`];
            setValidationErrors(newErrors);
        }
    };

    const handleOptionChange = (qIndex, optIndex, value) => {
        const updated = [...questions];
        updated[qIndex].options[optIndex] = value;
        setQuestions(updated);
    };

    const handleAddOption = (qIndex) => {
        const updated = [...questions];
        updated[qIndex].options.push("");
        setQuestions(updated);
    };

    const handleRemoveOption = (qIndex, optIndex) => {
        const updated = [...questions];
        if (updated[qIndex].options.length > 2) {
            updated[qIndex].options.splice(optIndex, 1);
            // Adjust answer index if necessary
            if (updated[qIndex].answerIndex === optIndex) {
                updated[qIndex].answerIndex = null;
            } else if (updated[qIndex].answerIndex > optIndex) {
                updated[qIndex].answerIndex -= 1;
            }
            setQuestions(updated);
        }
    };

    const handleRemoveQuestion = (qIndex) => {
        if (questions.length > 1) {
            const updated = [...questions];
            updated.splice(qIndex, 1);
            setQuestions(updated);
            if (expandedQuestion >= updated.length) {
                setExpandedQuestion(updated.length - 1);
            }
        }
    };

    const handleAnswerSelect = (qIndex, answerIndex) => {
        const updated = [...questions];
        updated[qIndex].answerIndex = answerIndex;
        setQuestions(updated);
    };

    const handleFormSubmit = (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        const json = {
            topic: registerData.topic,
            title: registerData.name,
            questions: questions
        };

        // Simulate API call since we don't have actual API access
        setIsSubmitting(true);
        setTimeout(() => {
            setIsSubmitting(false);
            setQuestions([{
                questionText: "",
                options: ["", ""],
                answerIndex: null,
                explanation: "",
            }]);
            handleSubmit({ 
                component: "micro_learning", 
                result: "MicroLearning content saved successfully!" 
            });
        }, 2000);
    };

    const renderBasicInfo = () => (
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box display="flex" alignItems="center" mb={3}>
                    <SchoolIcon sx={{ mr: 2, color: 'primary.main', fontSize: 28 }} />
                    <Typography variant="h6" fontWeight="600">
                        Basic Information
                    </Typography>
                </Box>
                
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            select
                            label="Select Client"
                            value={selectedClient}
                            onChange={(e) => setSelectedClient(e.target.value)}
                            SelectProps={{ native: true }}
                            fullWidth
                            variant="outlined"
                            error={!!validationErrors.client}
                            helperText={validationErrors.client}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                }
                            }}
                        >
                            <option value="">-- Select Client --</option>
                            {clientCodes.map((client, index) => (
                                <option key={index} value={client.name || client}>
                                    {client.name || client}
                                </option>
                            ))}
                        </TextField>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                        <TextField
                            select
                            label="Select Topic"
                            name="topic"
                            value={registerData.topic || ""}
                            onChange={handleChange}
                            SelectProps={{ native: true }}
                            fullWidth
                            variant="outlined"
                            error={!!validationErrors.topic}
                            helperText={validationErrors.topic}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                }
                            }}
                        >
                            <option value="">-- Select Topic --</option>
                            {topics.map((topic, index) => (
                                <option key={index} value={topic.name || topic}>
                                    {topic.name || topic}
                                </option>
                            ))}
                        </TextField>
                    </Grid>
                    
                    <Grid item xs={12}>
                        <TextField
                            label="Video Title"
                            name="name"
                            value={registerData.name || ''}
                            onChange={handleChange}
                            fullWidth
                            variant="outlined"
                            placeholder="Enter a descriptive title for your video"
                            error={!!validationErrors.name}
                            helperText={validationErrors.name || "This will be displayed to learners"}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                }
                            }}
                        />
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );

    const renderVideoUpload = () => (
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box display="flex" alignItems="center" mb={3}>
                    <VideoIcon sx={{ mr: 2, color: 'primary.main', fontSize: 28 }} />
                    <Typography variant="h6" fontWeight="600">
                        Video Upload
                    </Typography>
                </Box>
                
                <Box
                    sx={{
                        border: '2px dashed',
                        borderColor: 'primary.light',
                        borderRadius: 3,
                        p: 4,
                        textAlign: 'center',
                        backgroundColor: 'primary.50',
                        '&:hover': {
                            borderColor: 'primary.main',
                            backgroundColor: 'primary.100',
                        }
                    }}
                >
                    <VideoIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                    <TextField
                        type="file"
                        name="video"
                        onChange={(e) => setVideoFile(e.target.value)}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ accept: 'video/*' }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                            }
                        }}
                    />
                    <Typography variant="body2" color="text.secondary" mt={1}>
                        Upload your educational video (MP4, AVI, MOV supported)
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    );

    const renderQuestions = () => (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box display="flex" alignItems="center">
                    <QuizIcon sx={{ mr: 2, color: 'primary.main', fontSize: 28 }} />
                    <Typography variant="h6" fontWeight="600">
                        Assessment Questions
                    </Typography>
                </Box>
                <Chip 
                    label={`${questions.filter(q => isQuestionValid(q)).length}/${questions.length} Complete`}
                    color={questions.every(q => isQuestionValid(q)) ? "success" : "warning"}
                    size="small"
                />
            </Box>

            <Stack spacing={3}>
                {questions.map((q, qIndex) => (
                    <Card 
                        key={qIndex} 
                        elevation={2} 
                        sx={{ 
                            borderRadius: 3,
                            border: expandedQuestion === qIndex ? '2px solid' : '1px solid',
                            borderColor: expandedQuestion === qIndex ? 'primary.main' : 'divider',
                            transition: 'all 0.3s ease'
                        }}
                    >
                        <Box
                            sx={{
                                p: 2,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                cursor: 'pointer',
                                backgroundColor: expandedQuestion === qIndex ? 'primary.50' : 'transparent'
                            }}
                            onClick={() => setExpandedQuestion(expandedQuestion === qIndex ? -1 : qIndex)}
                        >
                            <Box display="flex" alignItems="center">
                                <Typography variant="subtitle1" fontWeight="600">
                                    Question {qIndex + 1}
                                </Typography>
                                {isQuestionValid(q) && (
                                    <CheckIcon sx={{ ml: 1, color: 'success.main', fontSize: 20 }} />
                                )}
                                {validationErrors[`question_${qIndex}`] && (
                                    <ErrorIcon sx={{ ml: 1, color: 'error.main', fontSize: 20 }} />
                                )}
                            </Box>
                            <Box display="flex" alignItems="center">
                                {questions.length > 1 && (
                                    <Tooltip title="Delete Question">
                                        <IconButton 
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleRemoveQuestion(qIndex);
                                            }}
                                            color="error"
                                            size="small"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {expandedQuestion === qIndex ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </Box>
                        </Box>

                        <Collapse in={expandedQuestion === qIndex}>
                            <CardContent sx={{ pt: 0 }}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                        <TextField
                                            label="Question Text"
                                            fullWidth
                                            multiline
                                            rows={2}
                                            value={q.questionText}
                                            onChange={(e) => handleQuestionChange(qIndex, "questionText", e.target.value)}
                                            placeholder="Enter your question here..."
                                            error={!!validationErrors[`question_${qIndex}`]}
                                            helperText={validationErrors[`question_${qIndex}`]}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 2,
                                                }
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" gutterBottom fontWeight="600">
                                            Answer Options
                                        </Typography>
                                        <Stack spacing={2}>
                                            <RadioGroup
                                                value={q.answerIndex !== null ? q.answerIndex.toString() : ''}
                                                onChange={(e) => handleAnswerSelect(qIndex, parseInt(e.target.value))}
                                            >
                                                {q.options.map((opt, optIndex) => (
                                                    <Box key={optIndex} display="flex" alignItems="center" gap={1}>
                                                        <FormControlLabel
                                                            value={optIndex.toString()}
                                                            control={<Radio size="small" />}
                                                            label=""
                                                            sx={{ mr: 0 }}
                                                        />
                                                        <TextField
                                                            label={`Option ${optIndex + 1}`}
                                                            value={opt}
                                                            onChange={(e) => handleOptionChange(qIndex, optIndex, e.target.value)}
                                                            fullWidth
                                                            size="small"
                                                            placeholder={`Enter option ${optIndex + 1}`}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    borderRadius: 2,
                                                                }
                                                            }}
                                                        />
                                                        {q.options.length > 2 && (
                                                            <IconButton 
                                                                onClick={() => handleRemoveOption(qIndex, optIndex)}
                                                                color="error"
                                                                size="small"
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        )}
                                                    </Box>
                                                ))}
                                            </RadioGroup>
                                        </Stack>
                                        
                                        <Box mt={2}>
                                            <Button 
                                                variant="outlined" 
                                                size="small"
                                                onClick={() => handleAddOption(qIndex)}
                                                startIcon={<AddIcon />}
                                                sx={{ borderRadius: 2 }}
                                            >
                                                Add Option
                                            </Button>
                                        </Box>

                                        {validationErrors[`answer_${qIndex}`] && (
                                            <Alert severity="error" sx={{ mt: 2 }}>
                                                {validationErrors[`answer_${qIndex}`]}
                                            </Alert>
                                        )}
                                    </Grid>

                                    <Grid item xs={12}>
                                        <TextField
                                            label="Explanation (Optional)"
                                            fullWidth
                                            multiline
                                            rows={2}
                                            value={q.explanation}
                                            onChange={(e) => handleQuestionChange(qIndex, "explanation", e.target.value)}
                                            placeholder="Provide an explanation for the correct answer..."
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 2,
                                                }
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Collapse>
                    </Card>
                ))}
            </Stack>

            <Box mt={3} textAlign="center">
                <Button 
                    variant="contained" 
                    onClick={handleAddQuestion}
                    startIcon={<AddIcon />}
                    size="large"
                    sx={{ 
                        borderRadius: 3,
                        px: 4,
                        py: 1.5,
                        textTransform: 'none',
                        fontSize: '1rem'
                    }}
                >
                    Add New Question
                </Button>
            </Box>
        </Box>
    );

    const renderReview = () => (
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box display="flex" alignItems="center" mb={3}>
                    <InfoIcon sx={{ mr: 2, color: 'primary.main', fontSize: 28 }} />
                    <Typography variant="h6" fontWeight="600">
                        Review & Submit
                    </Typography>
                </Box>
                
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">Client</Typography>
                        <Typography variant="body1" fontWeight="500">{selectedClient || 'Not selected'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">Topic</Typography>
                        <Typography variant="body1" fontWeight="500">{registerData.topic || 'Not selected'}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">Video Title</Typography>
                        <Typography variant="body1" fontWeight="500">{registerData.name || 'Not entered'}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">Questions</Typography>
                        <Typography variant="body1" fontWeight="500">
                            {questions.length} question{questions.length !== 1 ? 's' : ''} created
                        </Typography>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );

    const getCurrentStepContent = () => {
        switch (activeStep) {
            case 0:
                return renderBasicInfo();
            case 1:
                return renderVideoUpload();
            case 2:
                return renderQuestions();
            case 3:
                return renderReview();
            default:
                return renderBasicInfo();
        }
    };

    return (
        <Container maxWidth="lg" sx={{ py: 2 }}>
            <Paper 
                elevation={3} 
                sx={{ 
                    borderRadius: 4, 
                    overflow: 'hidden',
                    minHeight: isMobile ? 'calc(100vh - 32px)' : 'auto'
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        p: { xs: 2, sm: 3 },
                        position: 'relative'
                    }}
                >
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box>
                            <Typography variant={isMobile ? "h5" : "h4"} fontWeight="700">
                                {editData ? 'Edit' : 'Create'} MicroLearning
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                                Progress: {formProgress}% Complete
                            </Typography>
                        </Box>
                        <IconButton onClick={handleCloseModal} sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    
                    {/* Progress Bar */}
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            width: `${formProgress}%`,
                            height: 4,
                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            transition: 'width 0.5s ease'
                        }}
                    />
                </Box>

                {/* Stepper for larger screens */}
                {!isMobile && (
                    <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Stepper activeStep={activeStep} alternativeLabel>
                            {steps.map((label) => (
                                <Step key={label}>
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </Box>
                )}

                {/* Form Content */}
                <Box sx={{ p: { xs: 2, sm: 3 } }}>
                    <form onSubmit={handleFormSubmit}>
                        <Fade in={true} timeout={500}>
                            <Box>
                                {getCurrentStepContent()}
                            </Box>
                        </Fade>

                        {/* Navigation Buttons */}
                        <Box 
                            sx={{ 
                                mt: 4, 
                                display: 'flex', 
                                justifyContent: 'space-between',
                                flexDirection: isMobile ? 'column' : 'row',
                                gap: 2
                            }}
                        >
                            <Button
                                onClick={() => setActiveStep(Math.max(0, activeStep - 1))}
                                disabled={activeStep === 0}
                                variant="outlined"
                                size="large"
                                sx={{ 
                                    borderRadius: 3,
                                    px: 4,
                                    textTransform: 'none',
                                    order: isMobile ? 2 : 1
                                }}
                            >
                                Previous
                            </Button>

                            <Box sx={{ order: isMobile ? 1 : 2 }}>
                                {activeStep === steps.length - 1 ? (
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        size="large"
                                        disabled={isSubmitting}
                                        sx={{
                                            borderRadius: 3,
                                            px: 4,
                                            py: 1.5,
                                            textTransform: 'none',
                                            fontSize: '1rem',
                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            minWidth: 120
                                        }}
                                    >
                                        {isSubmitting ? (
                                            <CircularProgress size={24} sx={{ color: 'white' }} />
                                        ) : (
                                            'Submit'
                                        )}
                                    </Button>
                                ) : (
                                    <Button
                                        onClick={() => setActiveStep(Math.min(steps.length - 1, activeStep + 1))}
                                        variant="contained"
                                        size="large"
                                        sx={{
                                            borderRadius: 3,
                                            px: 4,
                                            py: 1.5,
                                            textTransform: 'none',
                                            fontSize: '1rem',
                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                        }}
                                    >
                                        Next
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    </form>
                </Box>
            </Paper>

            {/* Floating Action Button for mobile */}
            {isMobile && activeStep === 2 && (
                <Fab
                    color="primary"
                    onClick={handleAddQuestion}
                    sx={{
                        position: 'fixed',
                        bottom: 16,
                        right: 16,
                        zIndex: 1000
                    }}
                >
                    <AddIcon />
                </Fab>
            )}
        </Container>
    );
};

export default MicroLearningForm;